import { HttpMethod } from 'constants/'
import { useCallback } from 'react'
import { useHttpClientConfig } from '../useHttpClientConfig'
import { downloadBlob } from '../../../utils/downloadBlob'
import * as config from '../../../config'
import { CustomerProfileData } from 'hooks/useCustomerProfileData'

type DocumentType = 'customer-profile'

type GenerateDocumentOptions = {
  deliveryModel: string
  language: string
  solution: string
}

type GenerateDocumentQuery<T = CustomerProfileData, O = GenerateDocumentOptions> = {
  documentType: DocumentType
  data: T
  options: O
}

type GenerateDocumentResponse = {
  blob: Blob
  filename: string
}

interface UseHookContext {
  generateDocument: <T>(query: GenerateDocumentQuery<T>) => Promise<GenerateDocumentResponse>
  generateCustomerProfileDocument: (
    data: CustomerProfileData,
    options: GenerateDocumentOptions
  ) => Promise<GenerateDocumentResponse>
  downloadDocument: (response: GenerateDocumentResponse) => void
}

const useDocumentGenerationService = (): UseHookContext => {
  const { getConfig } = useHttpClientConfig()

  const getServiceUrl = useCallback(() => config.DOCUMENT_GENERATION_SERVICE_URL, [])

  const makeRequest = useCallback(
    async <T, O>(pathname: string, data: T, options: O): Promise<GenerateDocumentResponse> => {
      const baseUrl = getServiceUrl()
      const url = `${baseUrl}${pathname}`

      const httpConfig = await getConfig()

      const response = await fetch(url, {
        ...httpConfig,
        body: JSON.stringify({ data: data, options: options }),
        method: HttpMethod.POST,
      })

      if (!response.ok) {
        throw new Error(`Document generation failed: ${response.status} ${response.statusText}`)
      }

      const blob = await response.blob()

      const now = new Date()
      const dateString = now.toISOString().split('T')[0]
      const filename = `customer-profile-${dateString}.pdf`

      return {
        blob,
        filename,
      }
    },
    [getServiceUrl, getConfig]
  )

  const generateDocument = useCallback(
    <T>(query: GenerateDocumentQuery<T>) => {
      const country = 'DE' // use selectedCountry if more document templates are available
      const templateId = '1'

      const pathname = `/v1/document-generation-template/${country}/${query.documentType}/${templateId}/generate`

      return makeRequest(pathname, query.data, query.options)
    },
    [makeRequest]
  )

  const generateCustomerProfileDocument = useCallback(
    (data: CustomerProfileData, options: GenerateDocumentOptions) =>
      generateDocument({ data, documentType: 'customer-profile', options }),
    [generateDocument]
  )

  const downloadDocument = useCallback((response: GenerateDocumentResponse) => {
    downloadBlob(response.blob, response.filename)
  }, [])

  return {
    downloadDocument,
    generateCustomerProfileDocument,
    generateDocument,
  }
}

export { useDocumentGenerationService }
export type { GenerateDocumentQuery, GenerateDocumentResponse, DocumentType }
